// PoolHealthScorecardPanel.jsx — v1.0.2 Item 3
//
// Pool reliability scorecard. Generalized — picks pool dynamically.
// Wires to GET /api/admin/pool-health (no params) for the dropdown,
// then GET /api/admin/pool-health?pool_name=<name> for the data.
//
// Phase 5 appears in the dropdown automatically the moment its entry is
// uncommented in services/proportional_allocator.POOLS. Zero retrofit.
import { useEffect, useMemo, useState } from "react";
import { API, axios } from "@/api";
import PanelRefreshButton from "./PanelRefreshButton";
import { Loader2, ShieldCheck, DollarSign } from "lucide-react";

const COLOR_DOT = {
  green: "bg-emerald-500",
  yellow: "bg-amber-400",
  red: "bg-red-500",
  gray: "bg-slate-300",
};

// iter261 — Email Cascade Hit-Rate tile. Per-source activation counts for
// last night + last 7 days. Pulls from the extended /hunter-savings payload
// (cascade_activations.*). Zero new endpoints.
function EmailCascadeTile() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/hunter-savings`);
        if (alive) setData(r.data?.cascade_activations || null);
      } catch { /* hide silently */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!data) return null;
  const nightly = data.last_night || {};
  const weekly = data.last_7_days || {};
  // iter262 — per-$ efficiency
  const eff = data.efficiency_per_dollar || { last_night: {}, last_7_days: {} };
  const costs = data.cost_per_lookup_usd || {};
  const rows = [
    { key: "openweb_ninja", label: "OpenWeb Ninja", tag: "Option A" },
    { key: "outscraper", label: "Outscraper", tag: "Option B" },
    { key: "hunter", label: "Hunter.io", tag: "Option C" },
    { key: "abstractapi", label: "AbstractAPI", tag: "Option D" },
  ];
  const totalNight = rows.reduce((a, r) => a + (nightly[r.key] || 0), 0);

  const fmtEff = (srcKey, window) => {
    const cost = costs[srcKey];
    const e = eff[window]?.[srcKey];
    if (e === null || cost === 0) {
      return <span className="text-[9px] font-bold text-emerald-600">Free</span>;
    }
    if (typeof e !== "number") return <span className="text-slate-300">—</span>;
    return <span className="font-mono tabular-nums text-slate-600">{e.toLocaleString()}</span>;
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="email-cascade-tile">
      <div className="px-3 py-2 bg-gradient-to-r from-sky-50 to-emerald-50 border-b border-slate-200 flex items-center justify-between">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
          Email Cascade — last night
        </p>
        <span className="text-[10px] text-slate-500">{totalNight} total activations</span>
      </div>
      <table className="w-full text-[11px]">
        <thead>
          <tr className="bg-white text-slate-400 uppercase tracking-wider text-[9px]">
            <th className="px-3 py-1.5 text-left font-bold">Source</th>
            <th className="px-2 py-1.5 text-right font-bold">Last Night</th>
            <th className="px-2 py-1.5 text-right font-bold">Last 7d</th>
            <th className="px-2 py-1.5 text-right font-bold" title="emails found per $1 spent (last night)">Per $ (night)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.key}
              className="border-t border-slate-100"
              data-testid={`email-cascade-row-${r.key}`}
            >
              <td className="px-3 py-2">
                <span className="font-bold text-slate-700">{r.label}</span>
                <span className="ml-2 text-[9px] text-slate-400 uppercase">{r.tag}</span>
              </td>
              <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-700">
                {nightly[r.key] || 0}
              </td>
              <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-500">
                {weekly[r.key] || 0}
              </td>
              <td
                className="px-2 py-2 text-right"
                data-testid={`email-cascade-eff-${r.key}`}
              >
                {fmtEff(r.key, "last_night")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// v1.0.8 iter259 — Hunter credits saved by OpenWeb Ninja short-circuit.
// Renders a green ROI badge in the panel header. Consumes the existing
// /api/admin/hunter-savings endpoint (no new endpoints needed).
// iter261 — also returns `cascade_activations` used by EmailCascadeTile below.
function HunterSavingsBadge() {
  const [savings, setSavings] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/hunter-savings`);
        if (alive) setSavings(r.data);
      } catch {
        if (alive) setErr(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (err || !savings) return null;

  const week = savings.estimated_cost_savings_usd?.last_7_days ?? 0;
  const credits7d = savings.hunter_credits_saved?.last_7_days ?? 0;
  const tonight = savings.hunter_credits_saved?.tonight ?? 0;
  const allTime = savings.estimated_cost_savings_usd?.all_time ?? 0;

  const tooltip = `Hunter credits saved (OpenWeb Ninja + Outscraper short-circuit)
Tonight: ${tonight}
Last 7 days: ${credits7d} credits · $${week.toFixed(2)}
All-time: $${allTime.toFixed(2)}`;

  return (
    <span
      title={tooltip}
      className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
      data-testid="hunter-savings-badge"
    >
      <DollarSign className="w-3 h-3" />
      Saved ${week.toFixed(2)} this week
    </span>
  );
}

export default function PoolHealthScorecardPanel() {
  const [availablePools, setAvailablePools] = useState([]);
  const [poolName, setPoolName] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [error, setError] = useState(null);

  const loadPools = async () => {
    try {
      const r = await axios.get(`${API}/admin/pool-health`);
      const pools = r.data?.available_pools || [];
      setAvailablePools(pools);
      if (pools.length > 0 && !poolName) setPoolName(pools[0]);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const loadData = async (name) => {
    if (!name) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/pool-health`, { params: { pool_name: name } });
      setData(r.data);
      setLastRefreshedAt(Date.now());
    } catch (e) {
      setError(e?.response?.data?.detail?.error || e?.response?.data?.detail || e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPools(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (poolName) loadData(poolName); }, [poolName]);

  const refresh = () => loadData(poolName);

  const rows7d = useMemo(() => data?.windows?.["7d"] || [], [data]);
  const rows30d = useMemo(() => data?.windows?.["30d"] || [], [data]);
  const overallColor = useMemo(() => {
    if (!rows7d.length) return "gray";
    if (rows7d.some((r) => r.color === "red")) return "red";
    if (rows7d.some((r) => r.color === "yellow")) return "yellow";
    if (rows7d.some((r) => r.color === "green")) return "green";
    return "gray";
  }, [rows7d]);

  return (
    <div className="space-y-3" data-testid="pool-health-scorecard-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-500" />
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
            Pool
          </label>
          <select
            value={poolName}
            onChange={(e) => setPoolName(e.target.value)}
            className="text-xs bg-white border border-slate-200 rounded-md px-2 py-1 text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-200"
            data-testid="pool-health-pool-select"
          >
            {availablePools.length === 0 ? (
              <option value="">(no pools)</option>
            ) : (
              availablePools.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))
            )}
          </select>
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 ml-2">
            Overall
            <span className={`w-2.5 h-2.5 rounded-full ${COLOR_DOT[overallColor]}`} data-testid="pool-health-overall-dot" />
          </span>
          <HunterSavingsBadge />
        </div>
        <PanelRefreshButton onRefresh={refresh} lastRefreshedAt={lastRefreshedAt} testId="pool-health-refresh" />
      </div>

      {loading && (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-emerald-500" /></div>
      )}

      {error && !loading && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-2.5 text-xs text-red-700" data-testid="pool-health-error">
          Pool Health: {String(error)}
        </div>
      )}

      {!loading && !error && data && (
        <>
          <ScoreTable label="Last 7 days" rows={rows7d} testId="pool-health-7d" />
          <ScoreTable label="Last 30 days" rows={rows30d} testId="pool-health-30d" />
          <EmailCascadeTile />
          <p className="text-[10px] text-slate-400 text-center pt-1">
            Reliability = nights without redistribution / total nights active. Members: {data.members?.join(" · ")}
          </p>
        </>
      )}
    </div>
  );
}

function ScoreTable({ label, rows, testId }) {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid={testId}>
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{label}</p>
      </div>
      <table className="w-full text-[11px]">
        <thead>
          <tr className="bg-white text-slate-400 uppercase tracking-wider text-[9px]">
            <th className="px-3 py-1.5 text-left font-bold">Source</th>
            <th className="px-2 py-1.5 text-right font-bold">Nights Active</th>
            <th className="px-2 py-1.5 text-right font-bold">Nights Failed</th>
            <th className="px-2 py-1.5 text-right font-bold">Reliability</th>
            <th className="px-2 py-1.5 text-center font-bold">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-3 py-3 text-center text-[11px] text-slate-400 italic">
                No allocation data yet for this window.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={r.source_name} className="border-t border-slate-100" data-testid={`${testId}-row-${r.source_name}`}>
                <td className="px-3 py-2 font-bold text-slate-700">{r.source_name}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-700">{r.nights_active}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-500">{r.nights_failed}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-700">
                  {r.reliability_pct === null ? "—" : `${r.reliability_pct}%`}
                </td>
                <td className="px-2 py-2 text-center">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${COLOR_DOT[r.color] || COLOR_DOT.gray}`} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
